<template>
  <!-- Segmented control rather than a dropdown: with four options it is one
       click instead of two, and it shows the current language without opening. -->
  <div class="langs" role="group" :aria-label="t('nav.language')">
    <button
      v-for="language in LANGUAGES"
      :key="language.code"
      type="button"
      class="langs__item"
      :class="{ 'langs__item--active': locale === language.code }"
      :aria-pressed="locale === language.code"
      :title="language.name"
      @click="select(language.code)"
    >
      {{ language.label }}
    </button>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@general/auth/frontend'
import { isDemoInstance } from '@general/demo/frontend'
import { LANGUAGES, STORAGE_KEY } from '../app/languages'
import { preferenceSaver } from '../app/accountPreferences'

const { t, locale } = useI18n()
const auth = useAuthStore()

function select(code) {
  locale.value = code
  // The company profile sets the default for the instance; this is the personal
  // override. It is kept in the browser, for the first paint of the next load,
  // and on the account, so the person's other devices follow.
  localStorage.setItem(STORAGE_KEY, code)
  document.documentElement.lang = code
  preferenceSaver(auth.savePreferences, isDemoInstance)({ locale: code })
}
</script>

<style scoped>
.langs {
  display: flex;
  gap: 2px;
  padding: 2px;
  background: var(--color-gray-100);
  border-radius: var(--radius-sm);
}

.langs__item { flex: 1; }

.langs__item {
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  background: none;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--color-text-soft);
  transition: background var(--fast), color var(--fast);
}
.langs__item:hover { color: var(--color-text); }

.langs__item--active {
  background: var(--color-surface);
  color: var(--color-primary);
  box-shadow: var(--shadow-sm);
}

/* It used to be hidden on a phone, because four buttons ate the whole top bar.
   Now that it lives inside the user menu there is room for it everywhere, and
   a Catalan speaker on a phone gets the same choice as one on a laptop. */
</style>
